<template>
  <div class="container">
    <div v-if="unleashedError" class="row mt-4">
      <div class="col-md-24">
        <div class="alert alert-danger">
          <p>
            <strong
              >Whoops, it looks like there was a problem fetching data from
              Unleashed.</strong
            >
          </p>
          <p>
            This error has been automatically reported and we'll get to work
            ASAP.
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-24 m-2 p-4 text-center">
        <h1 class="h3">
          New Booking
        </h1>
        <button
          @click="showUnleashed()"
          v-if="hasUnleashed"
          class="btn btn-sm btn-primary"
        >
          <i class="fa fa-plus"></i> Unleashed Orders
        </button>
      </div>
    </div>

    <div v-if="serverError" class="alert alert-danger">
      <p><strong>Whoops, it looks like we're having some troubles.</strong></p>
      <p>This error has been reported and we'll get to work ASAP.</p>
      <p>
        Please <a href="/contact">contact us</a> directly for any orders you
        need placed.
      </p>
    </div>

    <form>
      <locations
        :locations="locations"
        :pickup="request.pickupInfo"
        :delivery="request.deliveryInfo"
        @locationUpdated="locationsUpdated"
      >
      </locations>

      <freight
        :freight="request.freight"
        :package-types="packageTypes"
        :has-chep-access="hasChepAccess"
        :delivery="request.deliveryInfo"
        @addFreightLine="addFreightLine"
        @removeFreightLine="removeFreightLine"
      >
      </freight>

      <section class="pickup">
        <div class="row">
          <div class="col-lg-16 mb-4">
            <pickup-time
              @restrictionToggled="restrictionToggled"
              :after="request.pickupInfo.after"
              :before="request.pickupInfo.before"
              :restriction="request.pickupInfo.restriction"
              class="bg-white overflow-hidden"
            ></pickup-time>
          </div>
          <div class="col-lg-8 mb-4">
            <file-upload
              :files="request.files"
              @uploaded="fileUploaded"
              @deleted="fileDeleted"
              class="bg-white overflow-hidden"
            ></file-upload>
          </div>
        </div>
      </section>
      <div class="t-text-lg">
        By placing this booking you agree to our
        <a
          href="/terms"
          target="_blank"
          class="t-text-blue-400 t-no-underline hover:t-text-blue-600"
          >terms</a
        >
        including strict claim requirements and $1,000 max liability per
        booking.
      </div>

      <div class="text-center mb-5 mt-3">
        <button
          v-if="loadingDeliveryDates"
          class="btn btn-success btn-lg"
          type="button"
          disabled
          v-html="loadingText.strings[loadingText.index]"
        ></button>
        <button
          v-if="!loadingDeliveryDates && errors.count() > 0"
          v-tooltip:top="[
            '<strong class=\'text-center\'>Please fix the errors above</strong>',
            true
          ]"
          class="btn btn-secondary disabled btn-lg"
          type="button"
        >
          View delivery date and price options
        </button>
        <button
          v-if="!loadingDeliveryDates && errors.count() == 0"
          class="btn btn-success btn-lg"
          type="button"
          @click="loadDeliveryDates"
        >
          View delivery date and price options
        </button>
      </div>

      <div class="confirmation" v-if="!loadingDeliveryDates && freightRequest">
        <div>
          <section class="delivery">
            <delivery-dates
              v-if="freightRequest.rates.data.length > 0"
              @rate-selected="changeRate"
              :selected="selectedRate"
              :rates="freightRequest.rates.data"
              :pricingResponseId="freightRequest.pricing_response_id"
            >
            </delivery-dates>
            <div v-else class="alert alert-warning">
              Oops, it looks like we couldn't find any rates for your search.
              We've been notified and will get in touch with a manual quote
              ASAP.<br /><br />
              <h6>For Urgent Queries</h6>
              <p>
                Please contact us via email or call us from our
                <a href="/contact">contact page</a>.
              </p>
            </div>
          </section>

          <section
            v-if="freightRequest.rates.data.length > 0"
            class="confirmation text-center bg-white"
          >
            <div
              class="alert alert-secondary d-inline-block m-4 pt-3 pb-3"
              v-html="pickupRangeText"
            ></div>

            <div class="form-check">
              <input
                v-model="lastBookingOfDay"
                type="checkbox"
                class="form-check-input"
                id="last-booking-of-day"
                style="height: 1.5rem;width: 1.5rem;"
              />
              <label
                class="form-check-label"
                for="last-booking-of-day"
                style="color: #ff0000;font-size: 1rem;margin-left: 1rem;"
                >Last booking for today
                <i
                  class="fa fa-info-circle"
                  aria-hidden="true"
                  v-tooltip.hover.focus="
                    'Checking this box helps Freighthub pickup your freight promptly and efficiently - Thanks!'
                  "
                ></i>
              </label>
            </div>

            <div class="text-center mb-5 mt-3" v-if="isSupremeCleaningBusiness">
                <button
                  v-if="placingBookingUpdateUnleashed"
                  disabled
                  class="btn btn-warning btn-lg"
                >
                  Placing booking <i class="fa fa-spinner fa-spin"></i>
                </button>
                <button
                  v-else
                  class="btn btn-warning btn-lg"
                  @click.prevent="placeBookingUpdateUnleashed"
                >
                  Confirm Booking and Update Unleashed
                </button>
            </div>

            <div class="form-row m-4 pb-4">
              <div
                class="form-group col-sm-12 col-md-8 offset-sm-0 offset-md-4"
              >
                <button
                  v-if="documentPreview.loading"
                  type="button"
                  class="btn btn-outline-secondary btn-lg btn-block"
                  disabled
                >
                  <i class="fa fa-spinner fa-spin"></i>
                </button>
                <button
                  v-else
                  type="button"
                  class="btn btn-outline-success btn-lg btn-block"
                  @click.prevent="loadDocumentPreview"
                >
                  Preview Documents
                </button>
              </div>
              <div class="form-group col-sm-12 col-md-8">
                <button
                  v-if="placingBooking"
                  disabled
                  class="btn btn-success btn-lg btn-block btn-block"
                >
                  Placing booking <i class="fa fa-spinner fa-spin"></i>
                </button>
                <button
                  v-else
                  class="btn btn-success btn-lg btn-block btn-block"
                  @click.prevent="placeBooking"
                >
                  Confirm Booking <i class="fa fa-check"></i>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </form>

    <modal
      v-if="documentPreview.showing"
      :showing="documentPreview.showing"
      size="large"
      @closed="documentPreview.showing = false"
    >
      <div class="modal-body" v-html="documentPreview.data"></div>
    </modal>

    <unleashed-orders
      v-if="unleashed.showing"
      @cancelled="unleashed.showing = false"
      @choosen="fillFromUnleashed"
      @directbooking="directBookingFromUnleashed"
    ></unleashed-orders>
  </div>
</template>

<script>
import moment from "moment";
import Locations from "./locations/Locations";
import Freight from "./freight/Freight";
import PickupTime from "./others/PickupTime";
import FileUpload from "./others/FileUpload";
import DeliveryDates from "./others/DeliveryDates";
import Modal from "../../utils/Modal";
import UnleashedOrders from "../unleashed/UnleashedOrders";

export default {
  name: "booking-form",
  components: {
    Locations,
    Freight,
    PickupTime,
    FileUpload,
    DeliveryDates,
    Modal,
    UnleashedOrders
  },
  $_veeValidate: {
    validator: "new"
  },

  props: [
    "bookingId",
    "hasUnleashed",
    "defaultLineDescription",
    "defaultFreightType",
    "hasChepAccess"
  ],

  data() {
    let today = moment();

    return {
      unleashedError: false,
      unleashed: {
        showing: false
      },
      documentPreview: {
        showing: false,
        data: null,
        loading: false
      },
      serverError: false,
      locations: {
        pickupLoading: true,
        deliveryLoading: true,
        pickup: [],
        delivery: []
      },
      freightRequest: null,
      loadingDeliveryDates: false,
      placingBooking: false,
      placingBookingUpdateUnleashed: false,
      placingBookingUpdateUnleashedDirectly: false,
      selectedRate: null,
      packageTypes: [],
      request: {
        pickupInfo: {
          location: {},
          reference: "",
          instructions: "",
          restriction: false,
          after: {
            time: today.format("HH") + ":00",
            date: today.format("YYYY-MM-DD")
          },
          before: {
            time: "17:00",
            date: today.add(1, "day").format("YYYY-MM-DD")
          }
        },
        deliveryInfo: {
          location: {},
          reference: "",
          instructions: ""
        },
        freight: [
          {
            quantity: 1,
            packageType: parseInt(this.defaultFreightType) || 1,
            chep: false,
            max_weight: null,
            max_volume: null,
            goodsType: 1,
            description: this.defaultLineDescription,
            weight: "",
            weight_by_number: 0,
            volume: "",
            volume_by_number: 0,
            dangerousGoods: [],
            other_desc: {
              default: this.defaultLineDescription,
              additional: null
            }
          }
        ],
        files: []
      },
      loadingText: {
        index: 0,
        strings: [
          "Analysing rate cards <i class='fa fa-spinner fa-spin'></i>",
          "Comparing carrier feedback <i class='fa fa-spinner fa-spin'></i>",
          "Tracking live truck capacities <i class='fa fa-spinner fa-spin'></i>"
        ]
      },
      placedBooking: {
        booking: {}
      },
      hasDgLine: false,
      showFallBackRate: false,
      lastBookingOfDay: false
    };
  },

  mounted() {
    let that = this;

    this.loadLocations();
    this.loadPackageTypes();

    Event.$on("removeError", function(fieldName) {
      that.errors.remove(fieldName);
    });

    Event.$on("pickupLocationChanged", function(location) {
      that.errors.remove("pickup_location");
      that.errors.remove("delivery_location");

      if (location.openingHours.data.length == 0) {
        that.errors.add({
          field: "pickup_location",
          msg: "Please add opening hours for this location"
        });
      }

      that.request.pickupInfo.location = location;
    });

    Event.$on("deliveryLocationChanged", function(location) {
      that.errors.remove("delivery_location");
      that.errors.remove("pickup_location");

      if (location.openingHours.data.length == 0) {
        that.errors.add({
          field: "delivery_location",
          msg: "Please add opening hours for this location"
        });
      }

      that.request.deliveryInfo.location = location;
    });

    this.$on("locationsSaved", this.loadLocations);

    document.addEventListener("keydown", function(event) {
      if (event.keyCode === 13) {
        that.loadDeliveryDates();
      }
    });

    if (this.bookingId) {
      this.fetchDraftBooking();
    }
  },

  methods: {
    loadPackageTypes() {
      axios
        .get("/api/package-types")
        .then(response => {
          this.packageTypes = response.data.data;

          this.request.freight[0].packageType =
            parseInt(this.defaultFreightType) || this.packageTypes[0].id;
          this.request.freight[0].max_weight = this.packageTypes[0].max_weight;
          this.request.freight[0].max_volume = this.packageTypes[0].max_volume;
        })
        .catch(error => {
          // console.log(error);
          Event.$emit("serverError");
        });
    },

    setFallBackRate() {
      this.loadingDeliveryDates = true;
      setTimeout(() => {
        this.loadingDeliveryDates = false;
        this.showFallBackRate = true;
      }, Math.floor(Math.random()) * Math.floor(5000) + 1500);
    },

    directBookingFromUnleashed(order) {

      this.placingBookingUpdateUnleashedDirectly = true;

      //close the unleashed order modal
      this.unleashed.showing = false;

      //open alert for loading..
      this.$swal({
          title: 'Loading...',
          text: 'Please wait',
          allowOutsideClick: false,
          onBeforeOpen: () => {
            this.$swal.showLoading();
          }
      });
      console.log("order direct booking", order);
      this.request.freight = [
        {
          quantity: order.FreightNumber,
          packageType: parseInt(this.defaultFreightType) || 1,
          max_weight: null,
          max_volume: null,
          chep: false,
          goodsType: 1,
          description: this.defaultLineDescription,
          weight: order.TotalWeight,
          volume: order.TotalVolume,
          dangerousGoods: [],
          other_desc: {
            default: this.defaultLineDescription,
            additional: null
          }
        }
      ];

      axios
        .get(
          `/send-freight/unleashed/customers/${order.CustomerCode}/${order.DeliveryStreetAddress}/${order.DeliveryStreetAddress2}`
        )
        .then(response => {
          this.request.deliveryInfo.location = response.data.data;
          this.request.deliveryInfo.instructions = order.Comments;
          this.request.pickupInfo.reference = order.OrderNumberInteger;

          const dispatchDate = moment(order.OrderDate);

          if (dispatchDate > moment()) {
            this.request.pickupInfo.after = {
              time: dispatchDate.format("HH") + ":00",
              date: dispatchDate.format("YYYY-MM-DD")
            };
          }

          let data = {
            pickup_location: this.request.pickupInfo.location.id,
            pickup_after:
              this.request.pickupInfo.after.date +
              " " +
              this.request.pickupInfo.after.time,
            pickup_restriction: this.request.pickupInfo.restriction,
            pickup_before:
              this.request.pickupInfo.before.date +
              " " +
              this.request.pickupInfo.before.time,
            pickup_reference: this.request.pickupInfo.reference,
            pickup_instructions: this.request.pickupInfo.instructions,

            delivery_location: this.request.deliveryInfo.location.id || null,
            delivery_reference: this.request.deliveryInfo.reference,
            delivery_instructions: this.request.deliveryInfo.instructions,

            freight: this.request.freight
          };

          return axios.post("/send-freight/new", data, {
                    headers: {
                      Accept: "application/json",
                      "Content-Type": "Application/json"
                    }
                  })

        })
        .then((response2) => {
            this.freightRequest = response2.data.data;
            console.log("New Freight Request Response Unleashed DB: ", this.freightRequest);

            let priceRates = response2.data.data.rates.data;
            let aramexRate;
            priceRates.forEach(function(rate) {
                if (rate.carrier_name === "Aramex" && !aramexRate) {
                  aramexRate = rate;
                }
            });
            this.selectedRate = aramexRate;
              
            // Check if the booking was fit for a courier booking but did not get courier rates
            // If this happens, it will lead to informing the user about the rates they got
            let maxCartons = 6;
            let allFreightLinesAreCartons = true;
            let allWeightsValid = true;
            let allVolumesValid = true;
            let hasDangerousGoods = false;

            let isValidCourierBooking = true;
            let freightLines = this.freightRequest.lines.data;

            console.log("freightLines", freightLines);

            let quantityCount = 0;
            for (let freightLine of freightLines) {
              quantityCount += freightLine.quantity;

              if (freightLine.packageType.data.id !== 16) {
                allFreightLinesAreCartons = false;
              } else if (freightLine.packageType.data.id === 16) {
                if (freightLine.weight / freightLine.quantity > 25) {
                  allWeightsValid = false;
                }
                if (freightLine.volume / freightLine.quantity > 0.125) {
                  allVolumesValid = false;
                }
              }

              if (freightLine.dgLines.data.length > 0) {
                hasDangerousGoods = true;
              }
            }

            if (
              quantityCount > maxCartons ||
              !allFreightLinesAreCartons ||
              !allWeightsValid ||
              !allVolumesValid ||
              hasDangerousGoods
            ) {
              isValidCourierBooking = false;
            }

            let hasCourierRate = false;
            for (let rate of this.freightRequest.rates.data) {
              if (rate.carrier_id === 1444) {
                hasCourierRate = true;
              }
            }

            if (isValidCourierBooking && !hasCourierRate) {
              this.$swal({
                title: "Regarding your rates",
                text:
                  "We think this booking is best delivered by courier however we were unable to get a courier rate." +
                  "Please contact us on (09) 280 3639 so we can help you. " +
                  "Or if the options below via truck suit you, please proceed with your booking.",
                type: "info",
                confirmButtonText: "Ok"
              }).then(() => {});
            }

            let placeBookingUpdateUnleashedParams = {
              request_id: this.freightRequest.id,
              rate_id: this.selectedRate.id,
              delivery_date: this.selectedRate.delivery_time,
              pickup_reference: this.request.pickupInfo.reference, //order number of unleashed
              booking_id: this.bookingId,
              unleashed: this.unleashed,
              last_booking_of_day: this.lastBookingOfDay
            }

            return axios.post("/send-freight/bookings/book-update-unleashed", placeBookingUpdateUnleashedParams);
        })
        .then((response3) => {
            console.log("book-update-unleashed response", response3);
            this.$swal.close();
            let booking = response3.data.data;
            this.bookingPlacedUnleashedUpdated(booking);
            this.checkAndDownloadLabel(`bookings/${booking.id}/address-label`);
            this.placingBookingUpdateUnleashedDirectly = false;
        })
        .catch(error => {
          this.unleashedError = true;
          console.error("error unleashed customer code", error);
          if (error.response && error.response.status == 422) {
            for (let fieldName in error.response.data.errors) {
              that.errors.add({
                field: fieldName,
                msg: error.response.data.errors[fieldName][0]
              });
            }
          }
          this.placingBookingUpdateUnleashedDirectly = false;
        });
        
    },

    fillFromUnleashed(order) {      
      // if there is a match between the unleashed location and the Freighthub delivery location
      if (order.MatchingLocation === 1) {
        this.locations.deliveryLoading = true;

         axios
          .get(
            `/send-freight/unleashed/customers/${order.CustomerCode}/${order.DeliveryStreetAddress}/${order.DeliveryStreetAddress2}`
          )
          .then(response => {
            this.locations.deliveryLoading = false;
            this.request.deliveryInfo.location = response.data.data;
            this.request.deliveryInfo.instructions = order.Comments;
          })
          .catch(error => {
            this.locations.deliveryLoading = false;
            this.unleashedError = true;
            console.error("Error on calling unleashed customer code api", error)
          });

          this.request.deliveryInfo.reference = order.CustomerRef;
      }
      this.request.pickupInfo.reference = order.OrderNumberInteger;

      this.unleashed = {
        showing: false,
        orderNumber: order.OrderNumber,
        shipmentNumber: order.ShipmentNumber,
        salesOrders: order,
      };
      
      //Handling for unleashed sales order that contains DGs
      let DGs = [];
      if (order.ContainsDg) {
        axios
          .get("/send-freight/dangerous-goods", {
            headers: { Accept: "application/json" }
          })
          .then(response => {
            let loadedDGs = response.data.data;
            
            order.ProductContainsDg.forEach(function(item) {
              let findDGs = loadedDGs.find(dg => dg.name === item.ProductNameFormatted);

              if (findDGs) {
                let findSize = findDGs.sizes.data.find(size => (parseFloat(size.amount) + size.unit) === item.UnitOfMeasure.Name);

                if (findSize) {
                    DGs.push({
                      size: findSize,
                      product: findDGs,
                      quantity: item.Quantity,
                    });
                } else {
                  alert('Your size of dangerous goods are not on the list. Please add it first and make sure it is correct');
                  window.location.reload();
                }
              } else {
                alert('Your dangerous goods are not on the list. Please add it first and make sure the name is correct');
                window.location.reload();
              }                
            })
          })
          .catch(error => {
            Event.$emit("serverError");
            console.error(error);
          });
      }

      this.request.freight = [
        {
          quantity: order.FreightNumber,
          packageType: parseInt(order.PackageTypeId), //parseInt(this.defaultFreightType) || 1,
          max_weight: null,
          max_volume: null,
          chep: false,
          goodsType: order.ContainsDg ? 11 : 1,
          description: this.defaultLineDescription,
          weight: order.TotalWeight,
          volume: order.TotalVolume,
          dangerousGoods: DGs,
          other_desc: {
            default: this.defaultLineDescription,
            additional: null
          }
        }
      ];

      var dispatchDate = moment(order.DispatchDate);

      if (dispatchDate > moment()) {
        this.request.pickupInfo.after = {
          time: dispatchDate.format("HH") + ":00",
          date: dispatchDate.format("YYYY-MM-DD")
        };
      }
    },

    locationsUpdated(event) {
      if (event && event.type === "pickup") {
        this.loadLocations(event.id);
      } else if (event && event.type === "delivery") {
        this.loadLocations(null, event.id);
      }
    },

    fetchDraftBooking() {
      axios
        .get("/send-freight/bookings/" + this.bookingId + "?includes=files")
        .then(response => {
          this.booking = response.data.data;

          if (this.booking.pickupLocation) {
            this.request.pickupInfo.location = this.booking.pickupLocation.data;
          }

          if (this.booking.deliveryLocation) {
            this.request.deliveryInfo.location = this.booking.deliveryLocation.data;
          }

          this.request.pickupInfo.reference = this.booking.sender_ref;
          this.request.pickupInfo.instructions = this.booking.pickup_instructions;

          this.request.deliveryInfo.reference = this.booking.receiver_ref;
          this.request.deliveryInfo.instructions = this.booking.delivery_instructions;

          if (this.booking.pickup_at_reg) {
            let pickupDay = moment(this.booking.pickup_at_reg).format(
              "YYYY-MM-DD"
            );

            if (pickupDay < moment().format("YYYY-MM-DD")) {
              pickupDay = moment().format("YYYY-MM-DD");
            }

            this.request.pickupInfo.after = {
              date: pickupDay,
              time: moment(this.booking.pickup_at_reg).format("HH") + ":00"
            };
          }

          if (this.booking.pickup_before_reg) {
            this.request.pickupInfo.restriction = true;

            this.request.pickupInfo.before = {
              date: moment(this.booking.pickup_before_reg).format("YYYY-MM-DD"),
              time: moment(this.booking.pickup_before_reg).format("HH") + ":00"
            };
          }

          if (this.booking.files) {
            this.request.files = this.booking.files.data;
          }

          if (this.booking.lines.data.length > 0) {
            this.request.freight = [];

            this.booking.lines.data.map(line => {
              let dangerousGoods = [];

              if (line.dgLines.data.length > 0) {
                line.dgLines.data.map(dgLine => {
                  dangerousGoods.push({
                    amount: dgLine.amount,
                    product: dgLine.product.data,
                    quantity: dgLine.quantity,
                    unit: dgLine.unit == "kg" ? "Kg" : "L"
                  });
                });
              }
              // else if (line.goodsType.data.id == 11)
              // {
              //   // Dangerous goods
              //   dangerousGoods.push({
              //     amount: null,
              //     quantity: 1,
              //     unit: "Kg",
              //     product: null
              //   });
              // }

              this.request.freight.push({
                description: line.description,
                other_desc: {
                  default: line.description,
                  additional: null
                },
                goodsType: line.goodsType.data.id,
                packageType: line.packageType.data.id,
                volume: line.volume,
                weight: line.weight,
                quantity: line.quantity,
                dangerousGoods: dangerousGoods
              });
            });
          }
        })
        .catch(error => {
          Event.$emit("serverError");
        });
    },

    bookingPlacedUnleashedUpdated(booking) {
      this.placingBookingUpdateUnleashed = false;
      var sawlTitle = "";
      var sawlText = "";

      if (!this.showFallBackRate) {
        if (booking.unleashed_updated) {
          sawlTitle = "Booking placed and unleashed updated!";
        } else {
           sawlTitle = "Booking placed and unleashed has not updated yet!";
        }
        sawlText = "We'll email you your documentation shortly.";
      } else {
        sawlTitle = "We're requesting a quote for you!";
        sawlText = "This booking is not yet confirmed.";
      }

      this.$swal({
        title: sawlTitle,
        text: sawlText,
        type: "success",
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'See booking <i class="fa fa-arrow-right"></i>',
        cancelButtonText: "Place another booking",
        cancelButtonColor: "#5bc0de",
        footer: `<a type="button" href="bookings/${
          booking.id
        }/address-label" target="_blank" id="print-address-label" class="swal2-cancel swal2-styled" aria-label="" style="display: inline-block; background-color: rgb(91, 192, 222);">Print address label</a>`
      }).then(response => {
        if (response.value) {
          window.location =
            "/send-freight/bookings/current?booking=" + booking.id;
        } else {
          location.reload();
        }
      });
    },

    bookingPlaced(booking) {
      this.placingBooking = false;
      var sawlTitle = "";
      var sawlText = "";

      if (!this.showFallBackRate) {
        sawlTitle = "Booking placed!";
        sawlText = "We'll email you your documentation shortly.";
      } else {
        sawlTitle = "We're requesting a quote for you!";
        sawlText = "This booking is not yet confirmed.";
      }

      this.$swal({
        title: sawlTitle,
        text: sawlText,
        type: "success",
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'See booking <i class="fa fa-arrow-right"></i>',
        cancelButtonText: "Place another booking",
        cancelButtonColor: "#5bc0de",
        footer: `<a type="button" href="bookings/${
          booking.id
        }/address-label" target="_blank" id="print-address-label" class="swal2-cancel swal2-styled" aria-label="" style="display: inline-block; background-color: rgb(91, 192, 222);">Print address label</a>`
      }).then(response => {
        if (response.value) {
          window.location =
            "/send-freight/bookings/current?booking=" + booking.id;
        } else {
          location.reload();
        }
      });
    },

    loadDocumentPreview() {
      this.documentPreview.loading = true;

      axios
        .get("/send-freight/" + this.freightRequest.id + "/preview-documents")
        .then(response => {
          this.documentPreview.loading = false;
          this.documentPreview.showing = true;
          this.documentPreview.data = response.data;
        })
        .catch(error => {
          Event.$emit("serverError");
        });
    },

    restrictionToggled(event) {
      if (this.request.pickupInfo.restriction) {
        return (this.request.pickupInfo.restriction = false);
      }

      return (this.request.pickupInfo.restriction = true);
    },

    fileUploaded(file) {
      this.request.files.push(file);
    },

    fileDeleted(index) {
      this.request.files.splice(index, 1);
    },

    removeFreightLine(index) {
      this.request.freight.splice(index, 1);
    },

    addFreightLine() {
      this.request.freight.push({
        quantity: 1,
        packageType:
          parseInt(this.defaultFreightType) || this.packageTypes[0].id,
        max_weight: this.packageTypes[0].max_weight,
        max_volume: this.packageTypes[0].max_volume,
        chep: false,
        goodsType: 1,
        description: this.defaultLineDescription,
        weight: "",
        weight_by_number: 0,
        volume: "",
        volume_by_number: 0,
        dangerousGoods: [],
        other_desc: {
          default: this.defaultLineDescription,
          additional: null
        }
      });
    },

    showServerError() {
      this.serverError = true;
    },

    loadLocations(pickupLocationId = null, deliveryLocationId = null) {
      this.errors.remove("pickup_location");
      this.errors.remove("delivery_location");
      this.locations.pickupLoading = true;
      this.locations.deliveryLoading = true;

      axios
        .get("/settings/locations/pickup")
        .then(response => {
          this.locations.pickup = response.data.data;

          let that = this;

          this.locations.pickup.map(function(location) {
            if (pickupLocationId) {
              if (pickupLocationId == location.id) {
                that.request.pickupInfo.location = location;
              }
            } else if (
              Object.keys(that.request.pickupInfo.location).length > 0
            ) {
              if (that.request.pickupInfo.location.id === location.id) {
                that.request.pickupInfo.location = location;
              }
            } else if (location.defaults.pickup) {
              that.request.pickupInfo.location = location;
            }

            that.locations.pickupLoading = false;
          });

          if (
            Object.keys(this.request.pickupInfo.location).length == 0 &&
            this.locations.pickup.length > 0
          ) {
            this.request.pickupInfo.location = this.locations.pickup[0];
          } else {
            that.locations.pickupLoading = false;
          }
        })
        .catch(this.showServerError);

      axios
        .get("/settings/locations/delivery")
        .then(response => {
          this.locations.delivery = response.data.data;
          let that = this;

          this.locations.delivery.map(function(location) {
            if (deliveryLocationId) {
              if (deliveryLocationId == location.id) {
                that.request.deliveryInfo.location = location;
              }
            } else if (that.request.deliveryInfo.location) {
              if (that.request.deliveryInfo.location.id === location.id) {
                that.request.deliveryInfo.location = location;
              }
            }

            that.locations.deliveryLoading = false;
          });

          if (this.locations.delivery.length == 0) {
            this.locations.deliveryLoading = false;
          }
        })
        .catch(this.showServerError);
    },

    changeRate(rate) {
      this.selectedRate = rate;
    },

    validateForm() {
      return this.$validator.validateAll();
    },

    checkDefaultLocationContactInfo(pickupLocation, deliveryLocation) {
      let type = null;
      if (
        pickupLocation.contact_name === null ||
        pickupLocation.phone === null
      ) {
        type = "pickup";
      }

      if (
        deliveryLocation.contact_name === null ||
        deliveryLocation.phone === null
      ) {
        type = "delivery";
      }

      if (
        (pickupLocation.contact_name === null ||
          pickupLocation.phone === null) &&
        (deliveryLocation.contact_name === null ||
          deliveryLocation.phone === null)
      ) {
        type = "pickup & delivery";
      }

      if (type) {
        this.$swal({
          title: `Please provide contact name and phone number at your ${type} location`,
          type: "warning"
        });
        return false;
      }
      return true;
    },

    loadDeliveryDates() {
      // let contactInfo = this.checkDefaultLocationContactInfo(
      //   this.request.pickupInfo.location,
      //   this.request.deliveryInfo.location
      // );

      // if (!contactInfo) {
      //   return;
      // }

      this.updateLoadingString();

      let that = this;

      if (Object.keys(this.request.deliveryInfo.location).length === 0) {
        this.errors.add({
          field: "delivery_location",
          msg: "Please select a delivery location"
        });

        return false;
      }

      if (
        this.request.pickupInfo.restriction &&
        this.request.pickupInfo.before.date +
          " " +
          this.request.pickupInfo.before.time <
          this.request.pickupInfo.after.date +
            " " +
            this.request.pickupInfo.after.time
      ) {
        this.errors.add({
          field: "pickup_before_datetime",
          msg: 'The "before" time must be later than the "after" time'
        });

        return;
      }

      this.validateForm().then(success => {
        if (success) {
          this.deliveryDates = [];
          this.loadingDeliveryDates = true;

          let data = {
            pickup_location: this.request.pickupInfo.location.id,
            pickup_after:
              this.request.pickupInfo.after.date +
              " " +
              this.request.pickupInfo.after.time,
            pickup_restriction: this.request.pickupInfo.restriction,
            pickup_before:
              this.request.pickupInfo.before.date +
              " " +
              this.request.pickupInfo.before.time,
            pickup_reference: this.request.pickupInfo.reference,
            pickup_instructions: this.request.pickupInfo.instructions,

            delivery_location: this.request.deliveryInfo.location.id || null,
            delivery_reference: this.request.deliveryInfo.reference,
            delivery_instructions: this.request.deliveryInfo.instructions,

            files: this.request.files,

            freight: this.request.freight
          };

          console.log(`Data: ${JSON.stringify(data)}`);

          //dg Unit Transformer: g->Kg & ml->L
          if (this.hasDgLine) {
            data = this.dgUnitTransformer(data);
          }

          axios
            .post("/send-freight/new", data, {
              headers: {
                Accept: "application/json",
                "Content-Type": "Application/json"
              }
            })
            .then(response => {
              this.freightRequest = response.data.data;
              console.log("Freight Request Response: ", this.freightRequest);
              // this.selectedRate = response.data.data.rates.data[0];

              let priceRates = response.data.data.rates.data;
              let firstRate;
              let aramexRate;
              priceRates.forEach(function(rate) {
                if (rate.carrier_name === "Aramex" && !aramexRate) {
                  aramexRate = rate;
                }

                if (!firstRate) {
                  firstRate = rate;
                }
              });
              this.selectedRate = aramexRate || firstRate;
              
              this.loadingDeliveryDates = false;
              this.$SmoothScroll(
                document.height !== undefined
                  ? document.height
                  : document.body.offsetHeight
              );

              // Check if the booking was fit for a courier booking but did not get courier rates
              // If this happens, it will lead to informing the user about the rates they got
              let maxCartons = 6;
              let allFreightLinesAreCartons = true;
              let allWeightsValid = true;
              let allVolumesValid = true;
              let hasDangerousGoods = false;

              let isValidCourierBooking = true;
              let freightLines = this.freightRequest.lines.data;

              // console.log("freightLines",freightLines);

              let quantityCount = 0;
              for (let freightLine of freightLines) {
                quantityCount += freightLine.quantity;

                if (freightLine.packageType.data.id !== 16) {
                  allFreightLinesAreCartons = false;
                } else if (freightLine.packageType.data.id === 16) {
                  if (freightLine.weight / freightLine.quantity > 25) {
                    allWeightsValid = false;
                  }
                  if (freightLine.volume / freightLine.quantity > 0.125) {
                    allVolumesValid = false;
                  }
                }

                if (freightLine.dgLines.data.length > 0) {
                  hasDangerousGoods = true;
                }
              }

              if (
                quantityCount > maxCartons ||
                !allFreightLinesAreCartons ||
                !allWeightsValid ||
                !allVolumesValid ||
                hasDangerousGoods
              ) {
                isValidCourierBooking = false;
              }

              //
              let userBusinessId = window.Freighthub.business.id;
              if (userBusinessId === 83 || userBusinessId === 98) {
                isValidCourierBooking = false;
              }

              let hasCourierRate = false;
              for (let rate of this.freightRequest.rates.data) {
                if (rate.carrier_id === 1444) {
                  hasCourierRate = true;
                }
              }

              if (isValidCourierBooking && !hasCourierRate) {
                this.$swal({
                  title: "Regarding your rates",
                  text:
                    "We think this booking is best delivered by courier however we were unable to get a courier rate." +
                    "Please contact us on (09) 280 3639 so we can help you. " +
                    "Or if the options below via truck suit you, please proceed with your booking.",
                  type: "info",
                  confirmButtonText: "Ok"
                }).then(() => {});
              }
            })
            .catch(error => {
              console.log(error);
              if (error.response && error.response.status == 422) {
                for (let fieldName in error.response.data.errors) {
                  that.errors.add({
                    field: fieldName,
                    msg: error.response.data.errors[fieldName][0]
                  });
                }
              }

              this.loadingDeliveryDates = false;
            });
        }
      });
    },

    checkAndDownloadLabel(url, maxRetries = 10, retryInterval = 3000) {
      // Function to initiate the download
      const downloadPDF = function(url) {
         let a = document.createElement('a');
          a.href = url;
          a.download = 'document.pdf';
          a.click();
      };

      // Function to check the file type
      const checkFileType = function() {
        fetch(url)
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/pdf')) {
              // It's a PDF file, initiate the download
              downloadPDF(url);
              console.log("File downloaded successuflly");
            } else {
              // It's not a PDF file, retry after a delay
              if (maxRetries > 0) {
                maxRetries--;
                setTimeout(checkFileType, retryInterval);
                console.log("maxRetries", maxRetries);
              } else {
                console.error('Max retries reached. File is not a PDF.');
              }
            }
          })
          .catch(error => {
            console.error('There was a problem with the fetch operation:', error);
          });
      };

      // Initial check
      checkFileType();
    },


    placeBookingUpdateUnleashed() {
      this.placingBookingUpdateUnleashed = true;
      // console.log("unleashed", this.unleashed);
      axios
        .post("/send-freight/bookings/book-update-unleashed", {
          request_id: this.freightRequest.id,
          rate_id: this.selectedRate.id,
          delivery_date: this.selectedRate.delivery_time,
          pickup_reference: this.request.pickupInfo.reference, //order number of unleashed
          booking_id: this.bookingId,
          unleashed: this.unleashed,
          last_booking_of_day: this.lastBookingOfDay
        })
        .then(response => {
          let booking = response.data.data;
          this.bookingPlacedUnleashedUpdated(booking);
          this.checkAndDownloadLabel(`bookings/${booking.id}/address-label`);
          // setTimeout(function () {
          //   let a = document.createElement('a');
          //   a.href = `bookings/${booking.id}/address-label`;
          //   a.download = 'download';

          //   a.click();
          // }, 17000);
        })
        .catch(error => {
          Event.$emit("serverError");
          console.error("error placeBookingUpdateUnleashed", error);
          this.placingBookingUpdateUnleashed = false;
        });
    },

    placeBooking() {
      this.placingBooking = true;
      // console.log(this.unleashed);
      axios
        .post("/send-freight/bookings", {
          request_id: this.freightRequest.id,
          rate_id: this.selectedRate.id,
          booking_id: this.bookingId,
          unleashed: this.unleashed,
          last_booking_of_day: this.lastBookingOfDay
        })
        .then(response => {
          this.bookingPlaced(response.data.data);
        })
        .catch(error => {
          Event.$emit("serverError");
          console.log(error);
          this.placingBooking = false;
        });
    },

    updateLoadingString() {
      setTimeout(() => {
        this.loadingText.index += 1;
        if (this.loadingText.index >= this.loadingText.strings.length) {
          this.loadingText.index = 0;
        }
        this.updateLoadingString();
      }, 3000);
    },

    dgUnitTransformer(data) {
      data.freight.forEach(function(freight) {
        if (freight.goodsType === 11) {
          freight.dangerousGoods.forEach(function(dangerousGood) {
            if (dangerousGood === undefined) {
              return;
            }
            if (dangerousGood.size) {
              dangerousGood.amount = dangerousGood.size.amount;
              dangerousGood.unit = dangerousGood.size.unit;
            }
          });
        }
      });
      return data;
    },

    showUnleashed() {
      this.unleashed.showing = true;
      this.unleashedError = false;
    }
  },

  computed: {
    isSupremeCleaningBusiness() {
      return window.Freighthub.business.id === 7;
    },
    pickupRangeText() {
      let pickupAfter = moment(
        this.request.pickupInfo.after.date +
          " " +
          this.request.pickupInfo.after.time,
        "YYYY-MM-DD HH-mm"
      ).format("h:mma ddd D MMM");

      let pickupBefore = "";

      if (this.request.pickupInfo.restriction) {
        pickupBefore =
          ", but <strong>before " +
          moment(
            this.request.pickupInfo.before.date +
              " " +
              this.request.pickupInfo.before.time,
            "YYYY-MM-DD HH-mm"
          ).format("h:mma ddd D MMM") +
          "</strong>";
      }

      let deliverBy = moment(
        this.selectedRate.delivery_time,
        "YYYY-MM-DD HH-mm"
      ).format("h:mma ddd D MMM");

      return (
        "Your freight will be picked up\
<strong> after " +
        pickupAfter +
        "</strong>" +
        pickupBefore +
        " and delivered <strong>by " +
        deliverBy +
        "</strong>"
      );
    },
    isFallBackRate() {
      return (
        this.freightRequest.rates.data.length > 0 &&
        this.freightRequest.rates.data[0].carrier_id == 5
      );
    }
  },

  watch: {
    "request.pickupInfo.location": function(newLocation) {
      if (
        this.booking &&
        this.booking.pickupLocation &&
        newLocation.id == this.booking.pickupLocation.data.id
      ) {
        this.request.pickupInfo.instructions = this.booking.pickup_instructions;
      } else {
        this.request.pickupInfo.instructions = newLocation.instructions;
      }
    },
    "request.deliveryInfo.location": function(newLocation) {
      if (this.unleashed.orderNumber) {
        return false;
      }

      if (
        this.booking &&
        this.booking.deliveryLocation &&
        newLocation.id == this.booking.deliveryLocation.data.id
      ) {
        this.request.deliveryInfo.instructions = this.booking.delivery_instructions;
      } else {
        this.request.deliveryInfo.instructions = newLocation.instructions;
      }
    },
    request: {
      handler() {
        this.freightRequest = null;
      },
      deep: true
    },
    "request.pickupInfo.restriction": function(newVal) {
      if (newVal === false) {
        this.errors.remove("pickup_before_datetime");
      }
    },
    "request.pickupInfo.before": {
      handler() {
        this.errors.remove("pickup_before_datetime");
      },
      deep: true
    },
    "request.pickupInfo.after": {
      handler() {
        this.errors.remove("pickup_before_datetime");
      },
      deep: true
    },
    "request.freight": {
      handler(freights) {
        this.hasDgLine = false;
        let that = this;
        freights.forEach(function(freight) {
          if (freight.goodsType === 11) {
            that.hasDgLine = true;
          }
        });
      },
      deep: true
    }
  }
};
</script>

<style scoped>
>>> label {
  font-size: 0.9rem;
}
</style>
